import React, { Fragment, useCallback, useMemo } from 'react';

import CustomerPlanSelect from './components/CustomerPlanSelect';
import { Divider } from '@mui/material';
import FeatureFlagSelect from './components/FeatureFlagSelect';
import MissingTranslations from './components/MissingTranslations';
import PropTypes from 'prop-types';
import { UI_VIEW_PREFIX } from '/b2b/authentication/constants';
import UserRoleSelect from './components/UserRoleSelect';
import { getTranslatedRoleLabel } from '/b2b/common/helpers/Roles';
import { makeStyles } from 'tss-react/mui';

const styles = theme => ({
    divider: {
        margin: theme.spacing(2, 0),
    },
});

const useStyles = makeStyles()(styles);

const AdminHelpers = props => {
    const {
        changeCustomerPlan,
        changeUserRole,
        customerId,
        customerPlan,
        featureFlags,
        featureFlagOptions,
        menuContainer,
        options,
        roles,
        setFeatureFlags,
    } = props;
    const { classes } = useStyles();

    const handleChangeCustomerPlan = useCallback(newPlan => {
        const newCustomerPlan = Object.entries(customerPlan).reduce((acc, [feature, value]) => {
            if (typeof value === 'boolean') {
                acc[feature] = newPlan.includes(feature);
            }
            return acc;
        }, {});
        changeCustomerPlan(newCustomerPlan);
    });

    const formattedPlanFeatures = useMemo(() => {
        return (
            Object.entries(customerPlan)
                // eslint-disable-next-line no-unused-vars
                .filter(([feature, enabled]) => enabled)
                .map(([feature]) => feature)
        );
    }, [customerPlan]);

    const formattedPlanFeatureOptions = useMemo(() => {
        return Object.entries(customerPlan)
            .map(([feature, enabled]) => ({
                enabled,
                feature,
                label: feature.replace(UI_VIEW_PREFIX, ''),
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [customerPlan]);

    const handleChangeUserRole = useCallback(newRoles => {
        if (newRoles.length) {
            // Do not allow empty roles
            changeUserRole(newRoles);
        }
    }, []);

    const formattedRoles = useMemo(() => {
        return roles.map(({ roleId }) => roleId);
    }, [roles]);

    const formattedRoleOptions = useMemo(() => {
        return options.map(({ roleId, role }) => ({
            id: roleId,
            name: getTranslatedRoleLabel({ value: role }),
        }));
    }, [options]);

    return (
        <Fragment>
            <MissingTranslations />
            <CustomerPlanSelect
                customerId={customerId}
                label="Customer Plan"
                menuContainer={menuContainer}
                options={formattedPlanFeatureOptions}
                value={formattedPlanFeatures}
                onChange={handleChangeCustomerPlan}
            />
            <UserRoleSelect
                customerId={customerId}
                menuContainer={menuContainer}
                options={formattedRoleOptions}
                value={formattedRoles}
                onChange={handleChangeUserRole}
            />
            <FeatureFlagSelect
                customerId={customerId}
                label="Feature Flags"
                menuContainer={menuContainer}
                options={featureFlagOptions}
                value={featureFlags}
                onChange={setFeatureFlags}
            />
            <Divider className={classes.divider} />
        </Fragment>
    );
};

AdminHelpers.propTypes = {
    changeCustomerPlan: PropTypes.func.isRequired,
    changeUserRole: PropTypes.func.isRequired,
    customerId: PropTypes.string,
    customerPlan: PropTypes.object,
    featureFlags: PropTypes.arrayOf(PropTypes.string),
    featureFlagOptions: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.string,
        })
    ),
    menuContainer: PropTypes.object,
    options: PropTypes.array,
    roles: PropTypes.array,
    setFeatureFlags: PropTypes.func.isRequired,
    userDetails: PropTypes.object.isRequired,
};

export default AdminHelpers;
